import { errors } from './error';

/**
 * Shared Sentry noise filters for browser ignoreErrors, SSR ignoreErrors /
 * unhandledRejection, and catch-site console guards (CaptureConsole).
 *
 * Catalog (keep visible here):
 * - Operational HTTP statuses: see OPERATIONAL_HTTP_STATUSES (503)
 * - Axios no-response messages: AXIOS_NO_RESPONSE_NOISE_MESSAGES
 * - Node/SSR upstream drops: OPERATIONAL_BOOTSTRAP_NETWORK_MESSAGES
 * - SSR-only Axios status messages: SSR_AXIOS_STATUS_IGNORE_MESSAGES
 */

/** HTTP statuses treated as operational (maintenance / closed shop), not defects. */
export const OPERATIONAL_HTTP_STATUSES = [503];

/**
 * Axios messages for aborted / offline / blocked XHR with no HTTP response.
 * Shared by browser ignoreErrors, SSR filters, and console noise guards.
 */
export const AXIOS_NO_RESPONSE_NOISE_MESSAGES = [
  'Network Error',
  'Request aborted',
];

/**
 * Node/SSR bootstrap upstream drops (no HTTP response). Keep out of browser
 * ignoreErrors — these are SSR/deploy operational noise.
 */
export const OPERATIONAL_BOOTSTRAP_NETWORK_MESSAGES = [
  'socket hang up',
  'Client network socket disconnected before secure TLS connection was established',
];

/**
 * Axios default messages for operational statused responses. SSR
 * ignoreErrors only — do not spread into browser `IGNORE_ERRORS`.
 */
export const SSR_AXIOS_STATUS_IGNORE_MESSAGES = [
  'Request failed with status code 503',
];

/**
 * SSR Sentry ignoreErrors: Axios no-response + Node bootstrap drops +
 * operational statused Axios messages. Do not spread into browser
 * `IGNORE_ERRORS`.
 */
export const SSR_SENTRY_IGNORE_ERRORS = [
  ...AXIOS_NO_RESPONSE_NOISE_MESSAGES,
  ...OPERATIONAL_BOOTSTRAP_NETWORK_MESSAGES,
  ...SSR_AXIOS_STATUS_IGNORE_MESSAGES,
];

/**
 * True when `error` looks like Axios no-response noise (not a statused API error).
 */
export function isAxiosNoResponseNoise(error) {
  if (!error || error.response) {
    return false;
  }
  const message = typeof error.message === 'string' ? error.message : '';
  return AXIOS_NO_RESPONSE_NOISE_MESSAGES.includes(message);
}

/**
 * True when `error` is a no-response Node/SSR upstream drop seen during
 * bootstrap (hang-up / TLS handshake disconnect).
 */
export function isOperationalBootstrapNetworkNoise(error) {
  if (!error || error.response) {
    return false;
  }
  const message = typeof error.message === 'string' ? error.message : '';
  return OPERATIONAL_BOOTSTRAP_NETWORK_MESSAGES.includes(message);
}

function isOperationalHttpStatus(status) {
  return OPERATIONAL_HTTP_STATUSES.includes(status);
}

/**
 * True when an Axios/upstream failure is an expected operational reason:
 * API maintenance/closed 503, Node upstream drop, or Axios no-response noise.
 * Used by bootstrap catches and catch-site console filtering.
 */
export function isOperationalAxiosFailure(error) {
  if (!error) {
    return false;
  }
  if (error.response && isOperationalHttpStatus(error.response.status)) {
    return true;
  }
  return (
    isOperationalBootstrapNetworkNoise(error) || isAxiosNoResponseNoise(error)
  );
}

/**
 * True when `store.lastError` (from `createErrorModel`) represents an
 * operational bootstrap failure. Raw Axios messages are not retained on the
 * store — use httpStatusCode / network publicMessage instead.
 */
export function isOperationalBootstrapLastError(lastError) {
  if (!lastError) {
    return false;
  }
  if (isOperationalHttpStatus(lastError.httpStatusCode)) {
    return true;
  }
  const publicMessage = lastError.publicMessage;
  if (!publicMessage) {
    return false;
  }
  if (typeof publicMessage === 'string') {
    return publicMessage === errors.networkError.id;
  }
  return publicMessage.id === errors.networkError.id;
}

/**
 * SSR unhandledRejection: skip statused Axios failures (existing),
 * no-response Network Error / Request aborted, and bootstrap Node network drops.
 */
export function shouldSkipSsrUnhandledRejection(reason) {
  if (!reason) {
    return false;
  }
  if (reason.response && reason.response.status >= 400) {
    return true;
  }
  return (
    isAxiosNoResponseNoise(reason) || isOperationalBootstrapNetworkNoise(reason)
  );
}

/**
 * Like `console.error`, but drops filtered Sentry noise so CaptureConsole /
 * browser Sentry are not fed those messages from catch sites (Axios
 * no-response, Node upstream drops, operational HTTP statuses such as 503).
 */
export function consoleErrorUnlessFiltered(...args) {
  if (args.some((arg) => isOperationalAxiosFailure(arg))) {
    return;
  }
  console.error(...args);
}
